// import type {
//   PaymentMethod as StripePaymentMethod,
//   Token,
// } from '@stripe/stripe-js';
import type { UserCards } from '@/api/customer/getCustomerCardsQuery';

import type { BnplMethodType } from './bnpl';

export const enum PaymentMethodType {
  payInPerson = 'payInPerson',
  applePay = 'applePay',
  googlePay = 'googlePay',
  card = 'card',
  cardless = 'cardless',
  cashApp = 'cashapp',
}

export type PaymentMethodPayInPerson = {
  type: PaymentMethodType.payInPerson;
  id: 'payInPerson';
};
export type PaymentMethodApplePay = {
  type: PaymentMethodType.applePay;
  id: 'applePay';
};
export type PaymentMethodGooglePay = {
  type: PaymentMethodType.googlePay;
  id: 'googlePay';
};
export type PaymentMethodCardGeneric = {
  type: PaymentMethodType.card;
  id: 'card';
};
export type PaymentMethodCashApp = {
  type: PaymentMethodType.cardless;
  id: 'cashapp';
  token: string;
};

export type PaymentMethodBnpl = {
  type: PaymentMethodType.cardless;
  id: 'bnpl';
  /**
   * Providers the BNPL sheet is opened with. The control layout renders one
   * row per eligible provider, so each row carries exactly one; the
   * experiment's treatment arms render a single provider-agnostic row that
   * carries every eligible provider and let Stripe pick (SQR-15886).
   */
  providers: BnplMethodType[];
};

// A new card which details were just provided by the user
export type PaymentMethodCardNew = {
  type: PaymentMethodType.card;
  id: string;
  token: string;
  cardDetails?: { brand: string; last4: string };
};

// A card previously saved to the user profile
export type PaymentMethodCardSaved = {
  type: PaymentMethodType.card;
  id: string;
  card: UserCards[0];
};

// for things like Stripe Link, Klarna, etc, which don't expose card details
export type PaymentMethodCardLess = {
  type: PaymentMethodType.cardless;
  id: 'link' | string; // payment methods may be enabled from the API side, thus this vague string type
  // token: StripePaymentMethod['id'];
};

/**
 * Instance of this type should have enough data to display it or apply to the cart.
 */
export type PaymentMethod =
  | PaymentMethodPayInPerson
  | PaymentMethodApplePay
  | PaymentMethodGooglePay
  | PaymentMethodCardGeneric
  | PaymentMethodCardNew
  | PaymentMethodCardSaved
  | PaymentMethodCardLess
  | PaymentMethodCashApp
  | PaymentMethodBnpl;

export const PAYMENT_METHOD_CARD_GENERIC = {
  type: 'card',
  id: 'card',
} as Readonly<PaymentMethodCardGeneric>;

export const PAYMENT_METHOD_APPLE_PAY = {
  type: 'applePay',
  id: 'applePay',
} as Readonly<PaymentMethodApplePay>;

export const PAYMENT_METHOD_GOOGLE_PAY = {
  type: 'googlePay',
  id: 'googlePay',
} as Readonly<PaymentMethodGooglePay>;

export const PAYMENT_METHOD_PAY_IN_PERSON = {
  type: 'payInPerson',
  id: 'payInPerson',
} as Readonly<PaymentMethodPayInPerson>;

export const PAYMENT_METHOD_CASH_APP = {
  type: 'cardless',
  id: 'cashapp',
} as Readonly<PaymentMethodCashApp>;

export const createBnplPaymentMethod = (
  providers: BnplMethodType[],
): PaymentMethodBnpl => ({
  type: PaymentMethodType.cardless,
  id: 'bnpl',
  providers,
});

export const isPaymentMethodCardGeneric = (
  method: PaymentMethod | undefined,
): method is PaymentMethodCardGeneric => {
  return (
    method?.type === PAYMENT_METHOD_CARD_GENERIC.type &&
    method?.id === PAYMENT_METHOD_CARD_GENERIC.id
  );
};

export const isPaymentMethodCardNew = (
  method: PaymentMethod | undefined,
): method is PaymentMethodCardNew => {
  return method?.type === 'card'; // && !!(method as PaymentMethodCardNew).token;
};

export const isPaymentMethodCardSaved = (
  method: PaymentMethod | undefined,
): method is PaymentMethodCardSaved => {
  return (
    method?.type === 'card' && !!(method as PaymentMethodCardSaved).card?.id
  );
};

export const isPaymentMethodCardLess = (
  method: PaymentMethod | undefined,
): method is PaymentMethodCardLess => {
  return method?.type === 'cardless';
};

export const isPaymentMethodSameNewCard = (
  a: PaymentMethod | undefined,
  b: PaymentMethod | undefined,
): boolean => {
  return (
    !!a &&
    !!b &&
    isPaymentMethodCardNew(a) &&
    isPaymentMethodCardNew(b) &&
    a.id === b.id
  );
};

export const isPaymentMethodDigitalWallet = (
  method: PaymentMethod | undefined,
): boolean => {
  return method?.type === 'applePay' || method?.type === 'googlePay';
};

export const isPaymentMethodCashApp = (
  method: PaymentMethod | undefined,
): method is PaymentMethodCashApp => {
  return (
    method?.type === PAYMENT_METHOD_CASH_APP.type &&
    method?.id === PAYMENT_METHOD_CASH_APP.id
  );
};

export const isPaymentMethodBnpl = (
  method: PaymentMethod | undefined,
): method is PaymentMethodBnpl => {
  return method?.type === PaymentMethodType.cardless && method?.id === 'bnpl';
};

/**
 * The provider a control BNPL row stands for. A treatment row offers several
 * and resolves the provider only inside the sheet, so it yields undefined.
 */
export const getBnplProvider = (
  method: PaymentMethod | undefined,
): BnplMethodType | undefined => {
  if (!isPaymentMethodBnpl(method) || method.providers.length !== 1) {
    return undefined;
  }
  return method.providers[0];
};

export const isSameBnplPaymentMethod = (
  a: PaymentMethod | undefined,
  b: PaymentMethod | undefined,
): boolean => {
  if (!isPaymentMethodBnpl(a) || !isPaymentMethodBnpl(b)) return false;
  return (
    a.providers.length === b.providers.length &&
    a.providers.every((provider, index) => provider === b.providers[index])
  );
};

const paymentMethodTypeToSnakeCase: Record<string, string> = {
  payInPerson: 'pay_in_person',
  applePay: 'apple_pay',
  googlePay: 'google_pay',
  card: 'card',
  cardless: 'cardless',
};

export const getTrackingMethod = (method: PaymentMethod): string => {
  if (method.type === PaymentMethodType.cardless && method.id === 'cashapp') {
    return 'cash_app';
  }
  if (method.type === PaymentMethodType.cardless && method.id === 'bnpl') {
    return 'bnpl';
  }
  return paymentMethodTypeToSnakeCase[method.type] ?? method.type;
};
